import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaignContributionsRequest } from 'modules/iterableGoals/ducks/actions';
import { getCampaignContributionsState } from 'modules/iterableGoals/ducks/selectors';
import { useGoalsTranslations } from 'modules/iterableGoals/hooks';
import { CampaignContributionsState, TabSectionPropType } from 'modules/iterableGoals/types';
import { SectionTitle } from '../../SectionTitle';
import { AnalyticsCard } from '../AnalyticsCard';
import { CampaignContributionTable } from './table';
import { ExportButton } from './ExportButton';

export const CampaignContribution = ({
  goalName,
  startTime,
  endTime,
  loading,
  error,
}: TabSectionPropType) => {
  const { t } = useGoalsTranslations();
  const campaignContributionState: CampaignContributionsState = useSelector(
    getCampaignContributionsState,
  );
  const {
    data: { info, results, totalSends } = {},
    input: { pagination, sort },
  } = campaignContributionState;

  const dispatch = useDispatch();

  const fetchCampaignContributionData = (
    goalIdentifier?: string,
    analyticsStartTime?: string,
    analyticsEndTime?: string,
  ) => {
    if (!goalIdentifier || !analyticsStartTime || !analyticsEndTime) return;

    dispatch(
      fetchCampaignContributionsRequest({
        goalName: goalIdentifier,
        startTime: analyticsStartTime,
        endTime: analyticsEndTime,
        pagination,
        sort,
      }),
    );
  };

  /**
   * Fetch new campaign contribution data as required
   */
  useEffect(() => {
    fetchCampaignContributionData(goalName, startTime, endTime);
  }, [
    goalName,
    startTime,
    endTime,
    pagination.offset,
    pagination.limit,
    sort.sortBy,
    sort.sortDirection,
  ]);

  return (
    <>
      <SectionTitle
        title={t('title.analytics.campaignContribution')}
        subTitle={t('title.analytics.campaignSend', { count: totalSends ?? 0 })}
        sectionComponent={
          goalName && startTime && endTime ? (
            <ExportButton
              goalName={goalName}
              startTime={startTime}
              endTime={endTime}
              sort={sort}
              isDisabled={(results?.length ?? 0) === 0}
            />
          ) : null
        }
      />
      <AnalyticsCard
        height={loading || error ? 596 : 'auto'}
        loading={loading}
        error={error}
        noPadding
        handleReload={() => fetchCampaignContributionData(goalName, startTime, endTime)}
      >
        {results?.length && (
          <CampaignContributionTable
            sort={sort}
            pagination={pagination}
            results={results}
            info={info}
          />
        )}
      </AnalyticsCard>
    </>
  );
};
